import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ActionIcon, AppShell, Box, Burger, Drawer, Group, Text, Tooltip, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import {
  IconBookmark,
  IconBookmarkFilled,
  IconChevronLeft,
  IconChevronRight,
  IconCopy,
  IconMoon,
  IconSun,
} from '@/components/icons'
import { useAuth } from '@/lib/auth-context'
import { useTheme } from '@/lib/theme-context'
import { HeaderUserToolbar } from '@/components/layout/HeaderUserToolbar'
import { SidebarBrand } from '@/components/layout/SidebarBrand'
import { AnimatedOutlet } from '@/components/layout/AnimatedOutlet'
import { WorkspaceTabBar } from '@/components/layout/WorkspaceTabBar'
import { SidebarNav } from '@/components/sidebar/SidebarNav'
import { SIDEBAR_NAV_LAYOUT } from '@/components/sidebar/sidebar-nav.styles'
import { pageTitleFromPath } from '@/lib/page-title'
import { buildRoutePath } from '@/lib/route-title'
import { BookmarksProvider } from '@/lib/bookmarks-context'
import { TicketBarStatsProvider, useTicketBarStats } from '@/lib/ticket-bar-stats-context'
import { TicketHeaderProvider, useTicketHeader } from '@/lib/ticket-header-context'
import { ShiftProvider } from '@/lib/shift-context'
import { useWorkspaceTabs, WorkspaceTabsProvider } from '@/lib/workspace-tabs-context'

const SIDEBAR_KEY = 'sidebarCollapsed'
/** Совпадает с `header.height` — блок лого в сайдбаре той же высоты */
const HEADER_HEIGHT = 48
/** Ширина с учётом компактного бренда + текста */
const NAV_EXPANDED = 260
const NAV_COLLAPSED = 52
const SIDEBAR_PAD = SIDEBAR_NAV_LAYOUT.padX
/** Полноценный сайдбар в потоке (свёрнутый/развёрнутый), без выезжающего оверлея — с этой ширины; как карточка обращения (1100px). */
const APP_SHELL_SIDEBAR_FULL_MIN_PX = 1100

function AppShellHeaderBar() {
  const { pathname } = useLocation()
  const { totals, loading } = useTicketBarStats()
  const wideChrome = useMediaQuery(`(min-width: ${APP_SHELL_SIDEBAR_FULL_MIN_PX}px)`) ?? false
  const isTickets = pathname === '/tickets' || pathname === '/ai-tickets'

  if (!isTickets) return null
  if (!wideChrome) return null

  const totalStr = loading ? '…' : totals != null ? (Number(totals.total) || 0).toLocaleString('ru-RU') : '—'
  const reqStr = loading ? '…' : totals != null ? (Number(totals.requireProcessing) || 0).toLocaleString('ru-RU') : '—'

  return (
    <Text
      component="div"
      size="xs"
      lh={1.2}
      ml="sm"
      style={{
        whiteSpace: 'nowrap',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 'min(52vw, 420px)',
      }}
      aria-live="polite"
    >
      <Text component="span" c="dimmed">
        Всего обращений:
      </Text>{' '}
      <Text component="span" fw={700} c="var(--mantine-color-text)">
        {totalStr}
      </Text>
      <Text component="span" c="dimmed" px={6}>
        ·
      </Text>
      <Text component="span" c="dimmed">
        Требуют обработки:
      </Text>{' '}
      <Text component="span" fw={700} c="var(--mantine-color-text)">
        {reqStr}
      </Text>
    </Text>
  )
}

/** Центр шапки: на карточке обращения — тема из активной вкладки + закладка и копирование ссылки из контекста. */
function AppHeaderCenterTitle() {
  const { pathname, search } = useLocation()
  const { tabs, activeTabId } = useWorkspaceTabs()
  const ticketHeaderCtx = useTicketHeader()
  const fallback = pageTitleFromPath(pathname, search || '')
  const isTicketDetail = /^\/tickets\/[^/]+$/.test(pathname)

  let display = fallback
  if (isTicketDetail) {
    const active = tabs.find((t) => t.id === activeTabId)
    const currentPath = buildRoutePath(pathname, search || '')
    if (active && buildRoutePath(active.pathname, active.search) === currentPath && active.title?.trim()) {
      display = active.title.trim()
    }
  }

  const titleMawFallback = isTicketDetail ? undefined : 'min(420px, 42vw)'
  const controls = isTicketDetail ? (ticketHeaderCtx?.controls ?? null) : null

  if (!controls) {
    return (
      <Text
        component="h1"
        size="sm"
        fw={600}
        lh={1.2}
        truncate
        maw={titleMawFallback}
        ta={isTicketDetail ? 'start' : 'center'}
        w={isTicketDetail ? '100%' : undefined}
        c="var(--mantine-color-text)"
        style={{ letterSpacing: '-0.02em' }}
        title={display}
      >
        {display}
      </Text>
    )
  }

  return (
    <Group
      gap={8}
      wrap="nowrap"
      align="center"
      justify="flex-start"
      w="100%"
      maw="100%"
      miw={0}
      style={{ pointerEvents: 'auto' }}
    >
      <Group gap={4} wrap="nowrap" align="center" style={{ flexShrink: 0 }}>
        <Tooltip label={controls.bookmarked ? 'Убрать из закладок' : 'В закладки'} withArrow>
          <ActionIcon
            variant="default"
            size="sm"
            loading={controls.bookmarkBusy}
            aria-label={controls.bookmarked ? 'Убрать из закладок' : 'В закладки'}
            onClick={controls.onBookmarkClick}
            style={{ flexShrink: 0 }}
          >
            {controls.bookmarked ? (
              <IconBookmarkFilled size={16} stroke={1.5} />
            ) : (
              <IconBookmark size={16} stroke={1.5} />
            )}
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Скопировать ссылку" withArrow>
          <ActionIcon
            variant="subtle"
            size="sm"
            aria-label="Скопировать ссылку"
            onClick={controls.onCopyLink}
            style={{ flexShrink: 0 }}
          >
            <IconCopy size={16} stroke={1.5} />
          </ActionIcon>
        </Tooltip>
      </Group>
      <Text
        component="h1"
        size="sm"
        fw={600}
        lh={1.2}
        truncate
        ta="start"
        c="var(--mantine-color-text)"
        style={{ flex: 1, minWidth: 0, letterSpacing: '-0.02em' }}
        title={display}
      >
        {display}
      </Text>
    </Group>
  )
}

export function AppLayout() {
  const { user, signOut } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const [mobileNavOpened, { toggle: toggleMobileNav, close: closeMobileNav }] = useDisclosure(false)
  /** Между `sm` и `APP_SHELL_SIDEBAR_FULL_MIN_PX`: меню только в Drawer поверх контента */
  const [compactNavOverlayOpened, { toggle: toggleCompactNavOverlay, close: closeCompactNavOverlay }] =
    useDisclosure(false)
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_KEY) === 'true')

  const toggleSidebar = useCallback(() => {
    setCollapsed((c) => {
      const next = !c
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? 'true' : 'false')
      } catch {
        /* ignore */
      }
      return next
    })
  }, [])

  const onSignOut = useCallback(() => {
    signOut()
    navigate('/login', { replace: true })
  }, [signOut, navigate])

  const navWidth = collapsed ? NAV_COLLAPSED : NAV_EXPANDED
  const { pathname } = useLocation()
  const isTicketDetailRoute = /^\/tickets\/[^/]+$/.test(pathname)
  const mantineTheme = useMantineTheme()
  const isSmUp = useMediaQuery(`(min-width: ${mantineTheme.breakpoints.sm})`) ?? false
  const isSidebarWideLayout = useMediaQuery(`(min-width: ${APP_SHELL_SIDEBAR_FULL_MIN_PX}px)`) ?? false
  const navWidthEffective = isSidebarWideLayout ? navWidth : NAV_COLLAPSED
  const sidebarCollapsedUi = isSidebarWideLayout ? collapsed : true
  /**
   * Ниже `sm` Mantine при `navbar.breakpoint` раздувает панель до 100% ширины (оверлей) —
   * вместе с нашим Drawer получается две навигации. На телефонах только Drawer, без AppShell.Navbar.
   */
  const desktopNavbarConfig = isSmUp
    ? {
        width: navWidthEffective,
        // Mobile navigation is handled by Drawer and the isSmUp guard above.
        // A second sm breakpoint makes Mantine expand Navbar over the page at exactly 768px.
        breakpoint: 0,
      }
    : undefined

  useEffect(() => {
    closeMobileNav()
    closeCompactNavOverlay()
  }, [pathname, closeMobileNav, closeCompactNavOverlay])

  useEffect(() => {
    if (isSmUp) closeMobileNav()
  }, [isSmUp, closeMobileNav])

  useEffect(() => {
    if (isSidebarWideLayout) closeCompactNavOverlay()
  }, [isSidebarWideLayout, closeCompactNavOverlay])

  useEffect(() => {
    if (!isSmUp) closeCompactNavOverlay()
  }, [isSmUp, closeCompactNavOverlay])

  const sidebarOverlayOpen = (!isSmUp && mobileNavOpened) || (isSmUp && !isSidebarWideLayout && compactNavOverlayOpened)

  const closeSidebarOverlay = useCallback(() => {
    closeMobileNav()
    closeCompactNavOverlay()
  }, [closeMobileNav, closeCompactNavOverlay])

  return (
    <ShiftProvider>
      <WorkspaceTabsProvider>
        <TicketBarStatsProvider>
          <BookmarksProvider>
            <TicketHeaderProvider>
              <AppShell
                padding={0}
                layout="alt"
                header={{ height: HEADER_HEIGHT }}
                navbar={desktopNavbarConfig}
                /* Высоту задаём снаружи; overflow — чтобы не росла страница целиком */
                style={{
                  flex: 1,
                  minHeight: 0,
                  height: '100%',
                  maxHeight: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                }}
                styles={{
                  header: {
                    paddingInline: 'var(--mantine-spacing-sm)',
                    zIndex: 200,
                    background: 'var(--mantine-color-body)',
                    borderBottom: '1px solid var(--mantine-color-default-border)',
                    transition: 'border-color 0.2s ease, box-shadow 0.25s ease',
                    boxShadow: '0 1px 0 color-mix(in srgb, var(--mantine-color-default-border) 35%, transparent)',
                  },
                  navbar: {
                    transition:
                      'width 240ms cubic-bezier(0.22, 1, 0.36, 1), min-width 240ms cubic-bezier(0.22, 1, 0.36, 1)',
                    borderRight: '1px solid light-dark(rgba(148,163,184,0.28), rgba(255,255,255,0.08))',
                    background:
                      'radial-gradient(circle at 0 0, light-dark(rgba(59,130,246,0.10), rgba(59,130,246,0.13)), transparent 34%), light-dark(#f8fafc, var(--mantine-color-dark-8))',
                    boxShadow: '8px 0 30px light-dark(rgba(15,23,42,0.04), rgba(0,0,0,0.18))',
                  },
                  /* Не min-height: 100dvh — иначе main растёт по контенту и ломает flex-цепочку к переписке */
                  main: {
                    minHeight: 0,
                    padding: 0,
                    paddingTop: 0,
                    paddingBottom: 0,
                    paddingInline: 0,
                  },
                }}
              >
                <AppShell.Header>
                  <Group
                    h="100%"
                    w="100%"
                    px={0}
                    justify="space-between"
                    align="center"
                    gap="sm"
                    wrap="nowrap"
                    aria-label="Панель управления"
                  >
                    <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
                      {!isSmUp ? (
                        <Burger
                          opened={mobileNavOpened}
                          onClick={toggleMobileNav}
                          size="sm"
                          aria-label={mobileNavOpened ? 'Закрыть меню' : 'Открыть меню'}
                        />
                      ) : !isSidebarWideLayout ? (
                        <Tooltip
                          label={compactNavOverlayOpened ? 'Закрыть меню' : 'Развернуть меню поверх экрана'}
                          position="bottom"
                          withArrow
                        >
                          <ActionIcon
                            variant="default"
                            size="sm"
                            radius="md"
                            aria-expanded={compactNavOverlayOpened}
                            aria-label={compactNavOverlayOpened ? 'Закрыть меню' : 'Развернуть меню'}
                            onClick={toggleCompactNavOverlay}
                          >
                            {compactNavOverlayOpened ? <IconChevronLeft size={16} /> : <IconChevronRight size={16} />}
                          </ActionIcon>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          label={collapsed ? 'Развернуть панель' : 'Свернуть панель'}
                          position="bottom"
                          withArrow
                        >
                          <ActionIcon
                            variant="default"
                            size="sm"
                            radius="md"
                            aria-expanded={!collapsed}
                            onClick={toggleSidebar}
                          >
                            {collapsed ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'} position="bottom" withArrow>
                        <ActionIcon variant="default" size="sm" radius="md" onClick={toggleTheme}>
                          {theme === 'dark' ? <IconSun size={16} /> : <IconMoon size={16} />}
                        </ActionIcon>
                      </Tooltip>
                      <AppShellHeaderBar />
                    </Group>
                    <Box
                      style={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isTicketDetailRoute ? 'flex-start' : 'center',
                        overflow: 'hidden',
                      }}
                    >
                      <AppHeaderCenterTitle />
                    </Box>
                    <Group gap="sm" wrap="nowrap" align="center" justify="flex-end" style={{ flexShrink: 0 }}>
                      {user ? <HeaderUserToolbar user={user} onSignOut={onSignOut} /> : null}
                    </Group>
                  </Group>
                </AppShell.Header>

                {isSmUp ? (
                  <AppShell.Navbar px={0} py={0}>
                    <AppShell.Section
                      h={HEADER_HEIGHT}
                      px={SIDEBAR_PAD}
                      style={{
                        flex: '0 0 auto',
                        display: 'flex',
                        alignItems: 'stretch',
                        boxSizing: 'border-box',
                        borderBottom: '1px solid light-dark(rgba(148,163,184,0.24), rgba(255,255,255,0.08))',
                        background: 'light-dark(rgba(255,255,255,0.72), rgba(26,27,30,0.72))',
                        backdropFilter: 'blur(8px)',
                      }}
                    >
                      <SidebarBrand collapsed={sidebarCollapsedUi} />
                    </AppShell.Section>

                    <AppShell.Section
                      grow
                      style={{
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box
                        style={{
                          flex: 1,
                          minHeight: 0,
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          WebkitOverflowScrolling: 'touch',
                        }}
                      >
                        <Box px={SIDEBAR_PAD} pt={2} pb="sm">
                          <Box
                            mt={6}
                            style={{
                              borderRadius: 'var(--mantine-radius-xl)',
                              background: sidebarCollapsedUi
                                ? 'transparent'
                                : 'light-dark(rgba(255,255,255,0.56), rgba(26,27,30,0.42))',
                              border: sidebarCollapsedUi
                                ? '1px solid transparent'
                                : '1px solid light-dark(rgba(148,163,184,0.20), rgba(255,255,255,0.06))',
                              overflow: 'hidden',
                            }}
                          >
                            <SidebarNav collapsed={sidebarCollapsedUi} />
                          </Box>
                        </Box>
                      </Box>
                    </AppShell.Section>
                  </AppShell.Navbar>
                ) : null}

                <AppShell.Main
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    minHeight: 0,
                    /* 100% ломается: корень AppShell в fixed-режиме без явной высоты — цикл с контентом */
                    height: '100dvh',
                    maxHeight: '100dvh',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    style={{
                      flex: '1 1 0%',
                      minHeight: 0,
                      minWidth: 0,
                      maxHeight: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      background: 'var(--mantine-color-body)',
                      boxSizing: 'border-box',
                      paddingTop: 'var(--app-shell-header-height)',
                      paddingBottom: 'var(--app-shell-footer-offset, 0rem)',
                      paddingInlineStart: 'var(--app-shell-navbar-offset, 0rem)',
                      paddingInlineEnd: 'var(--app-shell-aside-offset, 0rem)',
                    }}
                  >
                    <WorkspaceTabBar />
                    <Box
                      style={{
                        flex: '1 1 0%',
                        minHeight: 0,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                      }}
                    >
                      <AnimatedOutlet />
                    </Box>
                  </Box>
                </AppShell.Main>
              </AppShell>

              <Drawer
                opened={sidebarOverlayOpen}
                onClose={closeSidebarOverlay}
                position="left"
                size={NAV_EXPANDED}
                padding={0}
                title={null}
                zIndex={400}
                overlayProps={{ opacity: 0.45 }}
                styles={{
                  body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 },
                  /** Поверх шапки (z-index 200) и узкой полосы сайдбара */
                  content: { boxShadow: '4px 0 24px color-mix(in srgb, black 18%, transparent)' },
                }}
                keepMounted={false}
              >
                <Box
                  h={HEADER_HEIGHT}
                  px={SIDEBAR_PAD}
                  style={{
                    flex: '0 0 auto',
                    display: 'flex',
                    alignItems: 'stretch',
                    boxSizing: 'border-box',
                    borderBottom: '1px solid var(--app-shell-border-color)',
                  }}
                >
                  <SidebarBrand collapsed={false} />
                </Box>
                <Box
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  <Box px={SIDEBAR_PAD} pt={2} pb="sm">
                    <SidebarNav collapsed={false} />
                  </Box>
                </Box>
              </Drawer>
            </TicketHeaderProvider>
          </BookmarksProvider>
        </TicketBarStatsProvider>
      </WorkspaceTabsProvider>
    </ShiftProvider>
  )
}
